import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ButtonWrapper from './custom/ButtonWrapper'
import { eColor } from 'components/types'
import { developerFunction } from 'config/offerings'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import CheckIcon from '@mui/icons-material/Check'

export const FeaturesComparisonTable: FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const data = t('offerings.featuresTable', { returnObjects: true }) as {
    headers: string[]
    sections: {
      title: string
      rows: {
        label: string
        tooltip: string
        values: string[]
      }[]
    }[]
  }

  const isCheck = (val: string) => val === '✓' || val.toLowerCase() === 'yes'

  return (
    <div className="w-full max-w-[1600px] mx-auto mt-8">
      <Table className="w-full table-fixed border-collapse">
        <TableHead>
          <TableRow>
            <TableCell className="w-[25%] font-bold text-left align-middle px-4 py-3 text-lg">
              {t('Features')}
            </TableCell>

            {data.headers.map((header, i) => {
              const bgColors = ['bg-lime-300', 'bg-lime-500', 'bg-green-900']
              const textColors = ['text-black', 'text-black', 'text-white']

              // Custom width/margins per column
              const baseWidth = 'w-[94%]'
              const margin = i === 2 ? 'ml-3.5' : 'mx-auto' // Adjust 2nd and 3rd column if needed

              return (
                <TableCell
                  key={i}
                  className="w-[25%] align-middle p-0 sticky top-0 z-[1] bg-white"
                >
                  <div
                    className={`
          ${bgColors[i]} ${textColors[i]}
          rounded-t-xl
          ${baseWidth} ${margin}
          flex flex-col items-center justify-center
          py-4 px-2
        `}
                  >
                    <span className="font-semibold text-center">{header}</span>
                    {/* Desktop CTA */}
                    <div className="hidden lg:block mt-2">
                      <ButtonWrapper
                        color={eColor.secondary}
                        onClick={developerFunction}
                      >
                        {t('offerings.contactSales')}
                      </ButtonWrapper>
                    </div>
                    {/* Mobile CTA */}
                    <div className="block lg:hidden mt-2 w-full"></div>
                  </div>
                </TableCell>
              )
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.sections.map((section, sectionIdx) => (
            <>
              <TableRow key={`section-${sectionIdx}`} className="bg-lime-200">
                <TableCell
                  colSpan={data.headers.length + 1}
                  className="py-3 px-4 font-semibold text-md"
                >
                  {section.title}
                </TableCell>
              </TableRow>

              {section.rows.map((row, rowIdx) => {
                const isEvenRow = rowIdx % 2 === 1

                return (
                  <TableRow
                    key={`row-${sectionIdx}-${rowIdx}`}
                    className={isEvenRow ? 'bg-gray-100' : 'bg-white'}
                  >
                    <TableCell className="px-4 py-2 align-top border-r border-gray-300">
                      <div className="flex justify-between items-start">
                        <span className="text-left">{row.label}</span>
                        {!isSmallScreen && (
                          <Tooltip title={row.tooltip}>
                            <InfoOutlinedIcon
                              fontSize="small"
                              color={eColor.secondary}
                              className="ml-2 mt-[2px]"
                            />
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>

                    {(() => {
                      const cells = []

                      const isMergeableValue = (val: string) =>
                        val.trim() !== '✓' &&
                        val.trim().toLowerCase() !== 'yes' &&
                        val.trim() !== '-' &&
                        val.trim() !== '—' &&
                        val.trim() !== ''

                      for (
                        let colIdx = 0;
                        colIdx < row.values.length;
                        colIdx++
                      ) {
                        const val = row.values[colIdx]

                        // Merge Essentials + Standard if they are the same and are price-like
                        if (
                          colIdx === 0 &&
                          row.values[0] === row.values[1] &&
                          isMergeableValue(row.values[0])
                        ) {
                          cells.push(
                            <TableCell
                              key={`merged-${colIdx}`}
                              colSpan={2}
                              className="p-0 align-top border-r border-gray-300"
                            >
                              <div className="rounded-md w-full h-full px-3 py-2 shadow-sm text-center">
                                {row.values[1]}
                              </div>
                            </TableCell>
                          )
                          colIdx++ // Skip next column
                        } else if (
                          colIdx === 1 &&
                          row.values[0] === row.values[1] &&
                          isMergeableValue(row.values[0])
                        ) {
                          continue // skip, already merged
                        } else {
                          cells.push(
                            <TableCell
                              key={colIdx}
                              className={`p-0 align-top ${
                                colIdx < row.values.length - 1
                                  ? 'border-r border-gray-300'
                                  : ''
                              }`}
                            >
                              <div className="rounded-md w-full h-full px-3 py-2 shadow-sm text-center">
                                {isCheck(val) ? (
                                  <CheckIcon
                                    fontSize="small"
                                    className="text-green-600"
                                  />
                                ) : (
                                  val || '—'
                                )}
                              </div>
                            </TableCell>
                          )
                        }
                      }

                      return cells
                    })()}
                  </TableRow>
                )
              })}
            </>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
